import type { Patient } from '@clinic-os/shared/types';
import {
  DEMO_PATIENTS,
  DEMO_CLINIC_ID,
} from '../data/demo-data';

export interface PatientService {
  getAll: (clinicId?: string) => Promise<Patient[]>;
  getById: (id: string) => Promise<Patient | null>;
  create: (data: Omit<Patient, 'id' | 'createdAt' | 'updatedAt'>) => Promise<Patient>;
  update: (id: string, data: Partial<Patient>) => Promise<Patient>;
  delete: (id: string) => Promise<void>;
  search: (query: string, clinicId?: string) => Promise<Patient[]>;
}

class PatientServiceImpl implements PatientService {
  private patients: Patient[] = [...DEMO_PATIENTS];

  async getAll(clinicId = DEMO_CLINIC_ID): Promise<Patient[]> {
    return this.patients.filter((p) => p.clinicId === clinicId);
  }

  async getById(id: string): Promise<Patient | null> {
    return this.patients.find((p) => p.id === id) ?? null;
  }

  async create(data: Omit<Patient, 'id' | 'createdAt' | 'updatedAt'>): Promise<Patient> {
    const now = new Date().toISOString();
    const patient: Patient = {
      ...data,
      id: `p${Date.now()}`,
      createdAt: now,
      updatedAt: now,
    };
    this.patients.push(patient);
    return patient;
  }

  async update(id: string, data: Partial<Patient>): Promise<Patient> {
    const idx = this.patients.findIndex((p) => p.id === id);
    if (idx === -1) throw new Error(`Patient ${id} not found`);
    const updated: Patient = { ...this.patients[idx]!, ...data, updatedAt: new Date().toISOString() };
    this.patients[idx] = updated;
    return updated;
  }

  async delete(id: string): Promise<void> {
    this.patients = this.patients.filter((p) => p.id !== id);
  }

  async search(query: string, clinicId = DEMO_CLINIC_ID): Promise<Patient[]> {
    const q = query.toLowerCase();
    return this.patients.filter(
      (p) =>
        p.clinicId === clinicId &&
        (`${p.firstName} ${p.lastName}`.toLowerCase().includes(q) ||
          p.email?.toLowerCase().includes(q) ||
          p.phone?.includes(q))
    );
  }
}

export const patientService = new PatientServiceImpl();
